import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Play, RotateCcw, CheckCircle, AlertTriangle, Users, Activity,
  Shield, Zap, TrendingUp, Loader2, ChevronDown, ChevronRight,
  Eye, BarChart3, Brain, Droplets, Heart, Pill
} from 'lucide-react';

const SCENARIO_ICONS = {
  'Gut Instability Case': Droplets,
  'Medication Sensitivity Case': Pill,
  'Emotional Load Case': Brain,
  'Complex Mixed Case': Heart,
};

const READINESS_COLORS = {
  ready: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  nearly_ready: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  approaching: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  not_ready: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/20',
};

export default function DemoSimulation() {
  const [validation, setValidation] = useState(null);
  const [demoUsers, setDemoUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [valRes, usersRes] = await Promise.all([
        axios.get(`${API}/demo/validation`).catch(() => ({ data: null })),
        axios.get(`${API}/demo/users`).catch(() => ({ data: { users: [] } })),
      ]);
      setValidation(valRes.data);
      setDemoUsers(usersRes.data?.users || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/demo/seed`);
      if (res.data?.success) {
        toast.success('Demo simulation seeded successfully');
        await loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed');
    }
    setSeeding(false);
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL demo data? This will delete all demo users and simulation data.')) return;
    setResetting(true);
    try {
      const res = await axios.post(`${API}/demo/reset-all`);
      if (res.data?.success) {
        toast.success('All demo data purged');
        await loadData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setResetting(false);
  };

  const handleResetUser = async (userId) => {
    if (!window.confirm('Reset this demo user? All their simulation data will be deleted.')) return;
    try {
      await axios.post(`${API}/demo/reset/${userId}`);
      toast.success('Demo user reset. Re-seed to regenerate.');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const hasDemoData = validation && validation.totals?.users > 0;
  const subscribers = demoUsers.filter(u => u.role === 'subscriber');
  const staff = demoUsers.filter(u => u.role !== 'subscriber');

  if (loading) return (
    <AdminLayout>
      <div className="flex justify-center py-20" data-testid="demo-loading">
        <Loader2 className="w-6 h-6 text-adm-accent animate-spin" />
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6" data-testid="demo-simulation-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-xl font-['Outfit'] font-semibold text-adm-primary">Demo Simulation Layer</h1>
            <p className="text-xs text-adm-tertiary mt-1">Production-safe parallel reality for Care OS demonstration</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSeed}
              disabled={seeding}
              className="bg-adm-accent/15 text-adm-accent hover:bg-adm-accent/25 border border-adm-accent/20 text-xs h-8 px-3"
              data-testid="seed-demo-btn"
            >
              {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
              {hasDemoData ? 'Re-Seed' : 'Seed Demo'}
            </Button>
            {hasDemoData && (
              <Button
                onClick={handleResetAll}
                disabled={resetting}
                variant="ghost"
                className="text-rose-400 hover:bg-rose-500/10 text-xs h-8 px-3"
                data-testid="reset-all-btn"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                Reset All
              </Button>
            )}
          </div>
        </div>

        {/* Status Banner */}
        {!hasDemoData ? (
          <Card className="bg-adm-surface border-white/[0.06]">
            <CardContent className="py-12 flex flex-col items-center text-center">
              <Play className="w-10 h-10 text-adm-tertiary mb-4" />
              <h3 className="text-base font-['Outfit'] font-medium text-adm-primary mb-2">No Demo Data</h3>
              <p className="text-xs text-adm-tertiary max-w-md mb-4">
                Click "Seed Demo" to create 4 demo subscribers with 14 days of realistic biological scenarios,
                including pattern detection, predictive alerts, suggestions, and autonomous actions.
              </p>
              <Button
                onClick={handleSeed}
                disabled={seeding}
                className="bg-adm-accent text-adm-bg hover:bg-adm-accent/90 text-xs"
                data-testid="seed-empty-btn"
              >
                {seeding ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                Seed Demo Simulation
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Totals Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="demo-totals">
              {[
                { label: 'Users', value: validation.totals.users, icon: Users },
                { label: 'Tickets', value: validation.totals.tickets, icon: Activity },
                { label: 'Patterns', value: validation.totals.patterns, icon: TrendingUp },
                { label: 'Alerts', value: validation.totals.alerts, icon: AlertTriangle },
                { label: 'Suggestions', value: validation.totals.suggestions, icon: Zap },
                { label: 'Suppressed', value: validation.totals.suppressed, icon: Shield },
                { label: 'Auto-Actions', value: validation.totals.auto_actions, icon: Play },
                { label: 'Escalations', value: validation.totals.escalations, icon: AlertTriangle },
              ].map(({ label, value, icon: Icon }) => (
                <Card key={label} className="bg-adm-surface border-white/[0.06]">
                  <CardContent className="py-3 px-3 text-center">
                    <Icon className="w-3.5 h-3.5 text-adm-tertiary mx-auto mb-1" />
                    <div className="text-lg font-['Outfit'] font-semibold text-adm-primary">{value}</div>
                    <div className="text-[10px] text-adm-tertiary">{label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Readiness Distribution */}
            {validation.readiness_distribution && (
              <Card className="bg-adm-surface border-white/[0.06]">
                <CardContent className="py-4 px-4">
                  <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary mb-3">Activation Readiness Distribution</h3>
                  <div className="flex gap-3 flex-wrap">
                    {Object.entries(validation.readiness_distribution).map(([state, count]) => (
                      <Badge key={state} className={`${READINESS_COLORS[state]} text-xs px-3 py-1`}>
                        {state.replace('_', ' ')} — {count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Demo Staff */}
            {staff.length > 0 && (
              <Card className="bg-adm-surface border-white/[0.06]">
                <CardContent className="py-4 px-4">
                  <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary mb-3">Demo Staff</h3>
                  <div className="flex gap-3 flex-wrap">
                    {staff.map(s => (
                      <div key={s.id} className="flex items-center gap-2 bg-white/[0.03] rounded-lg px-3 py-2">
                        <Badge className="bg-adm-accent/15 text-adm-accent text-[10px]">DEMO</Badge>
                        <span className="text-xs text-adm-primary font-['Work_Sans']">{s.name}</span>
                        <span className="text-[10px] text-adm-tertiary capitalize">{s.role?.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Demo Subscribers — Story Cards */}
            <div className="space-y-3" data-testid="demo-subscribers">
              {(validation.demo_users || []).map((du) => {
                const ScenarioIcon = SCENARIO_ICONS[du.scenario] || Activity;
                const expanded = expandedUser === du.id;

                return (
                  <Card key={du.id} className="bg-adm-surface border-white/[0.06] overflow-hidden">
                    <CardContent className="p-0">
                      {/* Collapsed Header */}
                      <button
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
                        onClick={() => setExpandedUser(expanded ? null : du.id)}
                        data-testid={`demo-user-${du.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-adm-accent/10 flex items-center justify-center">
                            <ScenarioIcon className="w-4.5 h-4.5 text-adm-accent" />
                          </div>
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-['Outfit'] font-medium text-adm-primary">{du.name}</span>
                              <Badge className="bg-amber-500/15 text-amber-400 text-[9px] px-1.5 py-0">DEMO</Badge>
                            </div>
                            <span className="text-[11px] text-adm-tertiary">{du.scenario}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="hidden sm:flex items-center gap-3 text-[10px] text-adm-tertiary">
                            <span>{du.tickets} tickets</span>
                            <span>{Object.keys(du.patterns_detected || {}).length} patterns</span>
                            <span>{du.auto_actions} auto-actions</span>
                          </div>
                          {/* Readiness badges */}
                          <div className="hidden md:flex gap-1">
                            {(du.readiness || []).map((r, i) => (
                              <Badge key={i} className={`${READINESS_COLORS[r.state]} text-[9px] px-1.5 py-0`}>
                                {r.pattern.replace('_', ' ').split(' ').map(w => w[0].toUpperCase()).join('')}: {r.state.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                          {expanded ? <ChevronDown className="w-4 h-4 text-adm-tertiary" /> : <ChevronRight className="w-4 h-4 text-adm-tertiary" />}
                        </div>
                      </button>

                      {/* Expanded Detail */}
                      {expanded && (
                        <div className="px-4 pb-4 border-t border-white/[0.04] pt-3 space-y-4">
                          {/* Story Flow Narrative */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-['Outfit'] font-medium text-adm-accent uppercase tracking-wider">Story Flow</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {[
                                { step: '1. Initial State', desc: `${du.tickets} support tickets filed`, done: du.tickets > 0 },
                                { step: '2. Pattern Detection', desc: `${Object.keys(du.patterns_detected || {}).length} patterns found`, done: Object.keys(du.patterns_detected || {}).length > 0 },
                                { step: '3. Predictive Alerts', desc: `${du.alerts} alerts active`, done: du.alerts > 0 },
                                { step: '4. Suggestions', desc: `${du.suggestions} generated`, done: du.suggestions > 0 },
                                { step: '5. Suppressed Actions', desc: `${du.suppressed_actions} suppressed`, done: du.suppressed_actions > 0 },
                                { step: '6. Readiness', desc: (du.readiness || []).map(r => r.state).join(', ') || 'N/A', done: (du.readiness || []).length > 0 },
                                { step: '7. Auto-Actions', desc: `${du.auto_actions} executed`, done: du.auto_actions > 0 },
                                { step: '8. Rollback/Escalation', desc: `${du.rollback_events}R / ${du.escalation_events}E`, done: du.rollback_events > 0 || du.escalation_events > 0 },
                              ].map(({ step, desc, done }) => (
                                <div key={step} className={`rounded-lg p-2.5 border ${done ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-white/[0.02] border-white/[0.04]'}`}>
                                  <div className="flex items-center gap-1.5 mb-1">
                                    {done ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <div className="w-3 h-3 rounded-full border border-white/20" />}
                                    <span className="text-[10px] font-medium text-adm-primary">{step}</span>
                                  </div>
                                  <span className="text-[10px] text-adm-tertiary">{desc}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Pattern Details */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-['Outfit'] font-medium text-adm-accent uppercase tracking-wider">Detected Patterns</h4>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(du.patterns_detected || {}).map(([ptype, details]) => (
                                <div key={ptype} className="bg-white/[0.03] rounded-lg px-3 py-2 border border-white/[0.06]">
                                  <div className="text-xs font-medium text-adm-primary capitalize">{ptype.replace(/_/g, ' ')}</div>
                                  <div className="flex gap-3 mt-1">
                                    <span className="text-[10px] text-adm-tertiary">Confidence: <span className={details.confidence === 'high' ? 'text-emerald-400' : details.confidence === 'medium' ? 'text-amber-400' : 'text-zinc-400'}>{details.confidence}</span></span>
                                    <span className="text-[10px] text-adm-tertiary">Severity: {details.severity}</span>
                                    <span className="text-[10px] text-adm-tertiary">Sources: {(details.sources || []).join(', ')}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Readiness */}
                          <div className="space-y-2">
                            <h4 className="text-[11px] font-['Outfit'] font-medium text-adm-accent uppercase tracking-wider">Activation Readiness</h4>
                            <div className="flex flex-wrap gap-2">
                              {(du.readiness || []).map((r, i) => (
                                <div key={i} className="bg-white/[0.03] rounded-lg px-3 py-2 border border-white/[0.06]">
                                  <div className="text-xs font-medium text-adm-primary capitalize">{r.pattern.replace(/_/g, ' ')}</div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                                      <div className={`h-full rounded-full ${r.state === 'ready' ? 'bg-emerald-400' : r.state === 'nearly_ready' ? 'bg-amber-400' : r.state === 'approaching' ? 'bg-blue-400' : 'bg-zinc-400'}`} style={{ width: `${Math.min(r.score, 100)}%` }} />
                                    </div>
                                    <Badge className={`${READINESS_COLORS[r.state]} text-[9px] px-1.5 py-0`}>{r.score} — {r.state.replace('_', ' ')}</Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-400 hover:bg-rose-500/10 text-xs h-7"
                              onClick={() => handleResetUser(du.id)}
                              data-testid={`reset-user-${du.id}`}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" /> Reset User
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Isolation Rules Indicator */}
            <Card className="bg-adm-surface border-white/[0.06]">
              <CardContent className="py-4 px-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-xs font-['Outfit'] font-medium text-adm-primary">Isolation Rules Active</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    'Demo users excluded from pattern tuning logic',
                    'Demo users excluded from learning system updates',
                    'Demo users excluded from weekly intelligence reports',
                    'Demo users excluded from production analytics',
                    'Demo data fully isolated via is_demo flag',
                    'All demo data deletable via Reset',
                  ].map((rule, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-adm-tertiary">
                      <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                      {rule}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
